import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  generateExcelMultipleSheets,
  generateExcelTemplate,
  validateExcelData
} from "../data/excelData";

export const generateRandomDescription = () => {
  const taxDescriptions = [
    "Consulting Services",
    "Office Supplies",
    "Software License Fee",
    "Legal Advisory Fee",
    "Business Registration Fee",
    "Training & Development Course",
    "Annual Membership Subscription",
    "Equipment Purchase",
    "Marketing & Advertising Expense",
    "Travel & Accommodation",
    "Utility Bill Payment",
    "Vehicle Lease Payment",
    "Accounting Services",
    "Insurance Premium",
    "Maintenance & Repairs",
    "Courier & Shipping Charges",
    "Conference Registration Fee",
    "IT Support Services",
    "Audit Fee",
    "Tax Filing Service"
  ];

  return taxDescriptions[Math.floor(Math.random() * taxDescriptions.length)];
};

export const generateRandomAmount = () => {
  return Math.floor(Math.random() * 1000) + 1;
};

export const generateRandomYear = () => {
  const currentYear = new Date().getFullYear();
  return Math.floor(Math.random() * (currentYear - 2000 + 1)) + 2000;
};

export const generateRandomReferences = () => {
  const references = [
    () => `Invoice Number ${Math.floor(Math.random() * 1000) + 1}`,
    () => `Order ID ${Math.floor(Math.random() * 5000) + 1000}`,
    () => `Receipt #${Math.floor(Math.random() * 90000) + 10000}`,
    () => ""
  ];

  return references[Math.floor(Math.random() * references.length)]();
};

const useExcelUpload = ({
  fileName,
  instructions,
  sampleData,
  sheets,
  validationSchema,
  onUpload
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [errorFileUrl, setErrorFileUrl] = useState(null);
  const readerRef = useRef(null);
  const uploadingRef = useRef(false);

  useEffect(() => {
    return () => {
      if (errorFileUrl) URL.revokeObjectURL(errorFileUrl);
    };
  }, [errorFileUrl]);

  useEffect(() => () => {
    const reader = readerRef.current;
    readerRef.current = null;
    if (reader?.readyState === 1) reader.abort();
  }, []);

  const clearFile = () => {
    const reader = readerRef.current;
    readerRef.current = null;
    if (reader?.readyState === 1) reader.abort();
    setProcessing(false);
    setSelectedFile(null);
    setExcelData(null);
    setErrorFileUrl(null);
    setRowCount(0);
  };

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    clearFile();
    setOpenDialog(false);
  };

  const handleResetFile = () => {
    clearFile();
    setAlertMessage("");

    const fileInput = document.getElementById("fileInput");
    if (fileInput) fileInput.value = "";
  };

  const handleCloseAlert = () => setAlertMessage("");

  const readFile = (event, processWorkbook) => {
    const fileInput = event.target;
    const file = fileInput.files?.[0];

    if (!file || uploadingRef.current) return;

    clearFile();
    setSelectedFile(file);
    setAlertMessage("");
    setProcessing(true);

    fileInput.value = "";

    const reader = new FileReader();
    readerRef.current = reader;
    const failRead = () => {
      if (readerRef.current !== reader) return;
      readerRef.current = null;
      setExcelData(null);
      setRowCount(0);
      setErrorFileUrl(null);
      setProcessing(false);
      setAlertMessage(
        "Unable to read the Excel file. Check that it is a valid, unprotected workbook and select it again."
      );
      setAlertSeverity("error");
    };

    reader.onload = (e) => {
      if (readerRef.current !== reader) return;
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        if (!workbook.SheetNames.length) {
          throw new Error("The workbook contains no worksheets.");
        }
        processWorkbook(workbook);
      } catch (error) {
        failRead();
      } finally {
        if (readerRef.current === reader) {
          readerRef.current = null;
          setProcessing(false);
        }
      }
    };
    reader.onerror = failRead;
    reader.onabort = failRead;

    try {
      reader.readAsArrayBuffer(file);
    } catch (error) {
      failRead();
    }
  };

  const handleFileChange = (event) => {
    readFile(event, (workbook) => {
      // Assuming first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert sheet data to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Exclude header row
      const validRows = jsonData.slice(1).filter((row) => row.length > 0);

      if (validRows.length === 0) {
        setAlertMessage("No valid data found in the file.");
        setAlertSeverity("error");
      } else {
        if (validationSchema) {
          const { validRows, errors } = validateExcelData(
            jsonData.slice(1),
            validationSchema
          );

          if (errors.length > 0) {
            setAlertMessage(
              `Invalid data found. ${errors.length} issues detected.`
            );
            setAlertSeverity("warning");

            // Generate error report
            const errorText = errors.join("\n");
            const errorBlob = new Blob([errorText], {
              type: "text/plain"
            });
            const errorUrl = URL.createObjectURL(errorBlob);

            setErrorFileUrl(errorUrl);

            setExcelData(validRows);
            setRowCount(validRows.length);
          } else {
            setExcelData(validRows);
            setRowCount(validRows.length);
            setAlertMessage(
              `${validRows.length} rows found and validated successfully.`
            );
            setAlertSeverity("success");
          }
        } else {
          setExcelData(validRows);
          setRowCount(validRows.length);
          setAlertMessage(`${validRows.length} rows found in the file.`);
          setAlertSeverity("success");
        }
      }
    });
  };

  const handleFileMultiSheetChange = (event, sheetNames = []) => {
    readFile(event, (workbook) => {
      // If no sheetNames provided, read all sheets
      const targetSheetNames =
        sheetNames.length > 0 ? sheetNames : workbook.SheetNames;

      let allData = {};
      let totalRows = 0;
      let allErrors = [];

      targetSheetNames.forEach((sheet) => {
        if (!workbook.SheetNames.includes(sheet)) {
          allErrors.push(`Sheet "${sheet}" not found in workbook.`);
          return;
        }

        const worksheet = workbook.Sheets[sheet];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Exclude header row
        const validRows = jsonData.slice(1).filter((row) => row.length > 0);

        if (validRows.length === 0) {
          allErrors.push(`No valid data found in sheet "${sheet}".`);
        } else {
          if (validationSchema) {
            const { validRows: validatedRows, errors } = validateExcelData(
              jsonData.slice(1),
              validationSchema
            );

            if (errors.length > 0) {
              allErrors.push(...errors);
            }

            allData[sheet] = validatedRows;
            totalRows += validatedRows.length;
          } else {
            allData[sheet] = validRows;
            totalRows += validRows.length;
          }
        }
      });

      // Handle results
      if (Object.keys(allData).length === 0) {
        setAlertMessage("No valid data found in any sheet.");
        setAlertSeverity("error");
      } else {
        setExcelData(allData); // store as { sheetName: rows[] }
        setRowCount(totalRows);

        if (allErrors.length > 0) {
          setAlertMessage(`Some issues found. ${allErrors.length} warnings.`);
          setAlertSeverity("warning");

          // Generate error report
          const errorText = allErrors.join("\n");
          const errorBlob = new Blob([errorText], { type: "text/plain" });
          const errorUrl = URL.createObjectURL(errorBlob);

          setErrorFileUrl(errorUrl);
        } else {
          setAlertMessage(
            `${totalRows} rows found and validated successfully across ${targetSheetNames.length} sheet(s).`
          );
          setAlertSeverity("success");
        }
      }
    });
  };

  const upload = async (multiSheet = false) => {
    if (
      !selectedFile || !excelData || rowCount <= 0 ||
      readerRef.current || uploadingRef.current
    ) return;

    uploadingRef.current = true;
    setUploading(true);
    try {
      // Allow the progress indicator to render before mapping/importing rows.
      await new Promise((resolve) => setTimeout(resolve, 200));
      const result = multiSheet
        ? await onUpload(excelData)
        : await onUpload(excelData, rowCount);
      if (result === false) {
        setAlertMessage("The upload could not be saved. Please try again.");
        setAlertSeverity("error");
        return;
      }
      setAlertMessage(`Successfully uploaded ${rowCount} rows.`);
      setAlertSeverity("success");
      if (multiSheet) clearFile();
      else handleCloseDialog();
    } catch (error) {
      setAlertMessage("Unable to upload the Excel data. Please try again.");
      setAlertSeverity("error");
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  const handleUpload = () => upload();
  const handleUploadMultiSheet = () => upload(true);

  const handleDownloadTemplate = (options = {}) => {
    const {
      includeInstructions = true,
      includeSampleData = true,
      customFileName
    } = options;
    const name =
      customFileName && customFileName.trim()
        ? customFileName.trim()
        : fileName;
    const finalName = name.endsWith(".xlsx") ? name : `${name}.xlsx`;
    generateExcelTemplate({
      fileName: finalName,
      instructions,
      sampleData,
      includeInstructions,
      includeSampleData
    });
  };

  const handleDownloadMultipleTemplate = (options = {}) => {
    const { customFileName } = options;
    const name =
      customFileName && customFileName.trim()
        ? customFileName.trim()
        : fileName;
    const finalName = name.endsWith(".xlsx") ? name : `${name}.xlsx`;
    generateExcelMultipleSheets({ fileName: finalName, sheets });
  };

  const getTemplateHeadersString = () => {
    if (!sampleData || !Array.isArray(sampleData) || sampleData.length === 0)
      return "";
    const first = sampleData[0];
    return typeof first === "object" && first !== null && !Array.isArray(first)
      ? Object.keys(first).join("\t")
      : "";
  };

  return {
    openDialog,
    selectedFile,
    uploading,
    processing,
    rowCount,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    handleOpenDialog,
    handleCloseDialog,
    handleCloseAlert,
    handleFileChange,
    handleFileMultiSheetChange,
    handleResetFile,
    handleUpload,
    handleUploadMultiSheet,
    handleDownloadTemplate,
    handleDownloadMultipleTemplate,
    getTemplateHeadersString
  };
};

export default useExcelUpload;
